import React, { useEffect, useMemo } from 'react'
import styled, { ThemedStyledProps, withTheme } from 'styled-components'
import LoaderBase from 'react-loader-spinner'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import Contract from 'components/Contract'
import Lanes from 'components/Lanes'
import Alert from 'components/Alert'
import ConfirmationDialog from 'components/Dialogs/Confirmation'
import Tooltip from 'components/Tooltip'
import RFPFileActions from 'components/Carrier/RFPFileActions'
import { fetchRFP, submitRFP } from 'features/carrierProposal/actions'
import { fetchRateTypes } from 'features/rateTypes/actions'
import {
  lanesSelector,
  submittedAtSelector,
  requestForProposalSelector,
  rfpMetaSelector,
  errorSelector,
  laneErrorsSelector,
} from 'features/carrierProposal/selectors'
import { useModal } from 'hooks/useModal'
import useQueryParams from 'hooks/useQueryParams'
import { Theme } from 'styles/theme'

const Loader = styled(LoaderBase)`
  display: flex;
  justify-content: center;
`
const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-bottom: 14px;

  > :first-child {
    margin-right: 10px;
  }
`

function CarrierRFPDetails({
  theme,
}: ThemedStyledProps<Record<string, unknown>, Theme>) {
  const dispatch = useDispatch()
  const { linkUUID } = useParams<{ linkUUID: string }>()
  const { modalState, closeModal, openModal } = useModal()
  const lanes = useSelector(lanesSelector)
  const laneErrors = useSelector(laneErrorsSelector)
  const submittedAt = useSelector(submittedAtSelector)
  const requestForProposal = useSelector(requestForProposalSelector)
  const error = useSelector(errorSelector)
  const meta = useSelector(rfpMetaSelector)
  const queryParams = useQueryParams()
  const accepted = queryParams.get('accepted') === 'true'

  useEffect(() => {
    if (!linkUUID) return
    dispatch(fetchRFP(linkUUID, accepted))
    dispatch(fetchRateTypes())
  }, [accepted, linkUUID, dispatch])

  const submit = React.useCallback(() => {
    dispatch(submitRFP(linkUUID))
    closeModal()
  }, [linkUUID, dispatch, closeModal])

  const { hasCapacityErrors, hasRateErrors } = useMemo(() => {
    const errorsArray = Object.values(laneErrors)
    return {
      hasCapacityErrors: errorsArray.some((error) => error.capacity),
      hasRateErrors: errorsArray.some((error) => error.rate_price),
    }
  }, [laneErrors])

  if (meta.isLoading) return <Loader type="Oval" color={theme.colors.main} />

  return (
    <>
      <ConfirmationDialog
        title="Submit Offer?"
        warning="Are you ready to submit your offer?"
        description="After this you won't be able to change your offer."
        isOpen={modalState}
        closeModal={closeModal}
        confirm={submit}
      />
      {submittedAt && (
        <Alert type="success">
          <b>Your offer was successfully sent.</b>
          &nbsp; You will be notified when the shipper awards the lanes.
        </Alert>
      )}
      {error && (
        <Alert type="error" data-testid="alertError">
          <b>We could not send your offer.</b>
          &nbsp; {error}
        </Alert>
      )}
      {hasCapacityErrors && (
        <Alert type="error" data-testid="alertError">
          <b>One or more lanes have no capacity.</b>
          &nbsp; Enter a minimum number, or leave the price blank if you don’t
          want to bid on the lane.
        </Alert>
      )}
      {hasRateErrors && (
        <Alert type="error" data-testid="alertError">
          <b>One or more lanes have no rate.</b>
          &nbsp; If you don’t want to bid on the lane, delete the entry from the
          CSV file before uploading.
        </Alert>
      )}
      {!requestForProposal?.can_receive_proposals && !submittedAt && (
        <Alert type="info">
          <b>This RFP is no longer accepting offers.</b>
        </Alert>
      )}
      {requestForProposal && (
        <HeaderContainer>
          <Contract
            name={requestForProposal.name}
            startDate={requestForProposal.contract_start}
            endDate={requestForProposal.contract_end}
            submittedAt={submittedAt}
            submit={openModal}
            canReceiveProposals={requestForProposal.can_receive_proposals}
          />
          <RFPFileActions
            linkUUID={linkUUID}
            canReceiveProposals={
              requestForProposal.can_receive_proposals && !submittedAt
            }
          />
        </HeaderContainer>
      )}
      <Lanes
        lanes={lanes}
        isLocked={!!submittedAt || !requestForProposal?.can_receive_proposals}
      />
      <Tooltip />
    </>
  )
}

export default withTheme(CarrierRFPDetails)
